import React, { useEffect, useState } from 'react';
import {
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';
import ActionModal from 'ui-component/Modals/ActionModal';
import toast from 'react-hot-toast';

const EditCalculatorCategoryModal = ({ open, onClose, calculator, categories, onSave, onUploadIcon }) => {
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    useEffect(() => {
        if (open && calculator) {
            setSelectedCategory(calculator.categoryId || '');
            setLoading(false);
            setSelectedFile(null);
            setPreviewUrl('');
        }
    }, [open, calculator]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        const isPng =
            file.type === 'image/png' ||
            file.name.toLowerCase().endsWith('.png');

        if (!isPng) {
            toast.error('Only PNG files are allowed for calculator icons');
            event.target.value = '';
            setSelectedFile(null);
            setPreviewUrl('');
            return;
        }

        if (file.size > 100 * 1024) { // 100 KB
            toast.error('File must be smaller than 100KB');
            event.target.value = '';
            setSelectedFile(null);
            setPreviewUrl('');
            return;
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSave = async () => {
        if (!selectedCategory) {
            toast.error('Please select a category');
            return;
        }

        setLoading(true);
        try {
            if (selectedFile && onUploadIcon) {
                await onUploadIcon(calculator.id, selectedFile);
            }

            if (selectedCategory !== calculator.categoryId) {
                await onSave(calculator.id, selectedCategory);
            }

            onClose();
        } catch (err) {
            console.error('Save failed', err);
            toast.error('Failed to save changes');
        } finally {
            setLoading(false);
        }
    };

    return (
        <ActionModal
            open={open}
            onClose={onClose}
            title="Edit Calculator Category"
            actions={
                <>
                    <Button onClick={onClose} color="error" variant="contained" sx={{ mr: 1 }} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={loading || (selectedCategory === calculator.categoryId && selectedFile === null)}>
                        {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Save'}
                    </Button>
                </>
            }
        >
            <TextField
                fullWidth
                label="Calculator Name"
                value={calculator?.calculatorName || ''}
                disabled
                sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    {previewUrl || calculator?.iconUrl ? (
                        <Box
                            component="img"
                            src={previewUrl || calculator?.iconUrl}
                            alt={calculator?.calculatorName || 'Calculator icon'}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <Typography variant="caption" color="text.secondary" align="center">
                            No icon
                        </Typography>
                    )}
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Calculator Icon (PNG only, 50x50, ≤ 100KB)
                    </Typography>
                    <Button
                        variant="outlined"
                        component="label"
                        disabled={loading}
                        size="small"
                    >
                        Choose PNG
                        <input
                            type="file"
                            accept="image/png"
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                    {selectedFile && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {selectedFile.name}
                        </Typography>
                    )}
                </Box>
            </Box>

            <TextField
                select
                fullWidth
                label="Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={loading}
            >
                {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                    </MenuItem>
                ))}
            </TextField>
        </ActionModal>
    );
};
export default EditCalculatorCategoryModal;